// Support ticket flow: capture a screenshot, upload it to Storage, and write a
// `supportTickets` doc. A Firestore trigger (process_support_ticket) turns the
// doc into a branded support email with the screenshot attached.
//
// Screenshot capture is best-effort and reliability-tiered:
//   1. Native (Capacitor) screenshot plugin when running in the installed app
//      — captures the real composited screen (maps, video, native layers).
//   2. html2canvas on the web — captures the DOM (maps/video may be blank, but
//      it's good enough for most UI bug reports).
//   3. No screenshot — the ticket still goes through with diagnostics only.

import { Capacitor, registerPlugin } from "@capacitor/core";
import { ref, uploadString } from "firebase/storage";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { auth, db, storage } from "../firebase";
import { APP_VERSION } from "../appVersion";
import { authedWrite } from "../utils/authedWrite";

// Optional native plugin. registerPlugin returns a proxy that rejects with
// "not implemented" on platforms where the plugin isn't installed, so this is
// safe to reference even though no native Screenshot plugin ships today — the
// web fallback takes over until one is added to the Capacitor builds.
const ScreenshotPlugin = registerPlugin("Screenshot");

function isNative() {
  try {
    return !!Capacitor?.isNativePlatform?.();
  } catch {
    return false;
  }
}

// Downscale + compress a data URL to a compact JPEG. Keeping the image small is
// important: the support trigger inlines the (base64-encoded) screenshot into a
// `mail` document, which is subject to Firestore's ~1 MiB document limit. A
// 1280px-wide JPEG at q0.6 comfortably stays well under that ceiling while
// remaining legible for bug triage.
const MAX_SHOT_DIM = 1280;
const SHOT_QUALITY = 0.6;

function downscaleDataUrl(dataUrl, maxDim = MAX_SHOT_DIM, quality = SHOT_QUALITY) {
  return new Promise((resolve) => {
    if (!dataUrl || typeof document === "undefined") {
      resolve(dataUrl || null);
      return;
    }
    const img = new Image();
    img.onload = () => {
      try {
        const { width, height } = img;
        if (!width || !height) {
          resolve(dataUrl);
          return;
        }
        const scale = Math.min(1, maxDim / Math.max(width, height));
        const w = Math.max(1, Math.round(width * scale));
        const h = Math.max(1, Math.round(height * scale));
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        // White matte so transparent regions don't turn black in JPEG.
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, w, h);
        ctx.drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL("image/jpeg", quality));
      } catch {
        resolve(dataUrl);
      }
    };
    img.onerror = () => resolve(dataUrl);
    img.src = dataUrl;
  });
}

async function captureNativeScreenshot() {
  if (!isNative()) return null;
  try {
    const shot = await ScreenshotPlugin.take();
    if (!shot) return null;
    if (shot.dataUrl) return shot.dataUrl;
    if (shot.base64) {
      const base64 = String(shot.base64);
      return base64.startsWith("data:") ? base64 : `data:image/png;base64,${base64}`;
    }
  } catch {
    // Plugin not installed / not implemented — fall back to html2canvas.
  }
  return null;
}

async function captureWebScreenshot() {
  if (typeof document === "undefined") return null;
  try {
    // Dynamic import keeps html2canvas out of the main bundle (separate chunk,
    // only fetched when a user actually opens the support flow).
    const mod = await import("html2canvas");
    const html2canvas = mod.default || mod;
    const canvas = await html2canvas(document.body, {
      useCORS: true,
      allowTaint: false,
      logging: false,
      backgroundColor: null,
      scale: Math.min(2, (typeof window !== "undefined" && window.devicePixelRatio) || 1),
    });
    return canvas.toDataURL("image/png");
  } catch (err) {
    console.warn("[support] web screenshot capture failed:", err?.message || err);
    return null;
  }
}

/**
 * Capture a screenshot of the current app view. Returns a PNG data URL, or
 * null if capture isn't possible. Call this BEFORE opening any support overlay
 * so the overlay isn't included in the capture.
 */
export async function captureScreenshot() {
  const native = await captureNativeScreenshot();
  const raw = native || (await captureWebScreenshot());
  if (!raw) return null;
  return downscaleDataUrl(raw);
}

/**
 * Upload an optional screenshot and write a support ticket. The signed-in user
 * is the ticket owner; a Firestore trigger emails support with the diagnostics
 * and screenshot attached.
 *
 * @returns {Promise<{ ticketId: string, hadScreenshot: boolean }>}
 */
export async function submitSupportTicket({
  description = "",
  context = "",
  sessionId = "",
  screenshotDataUrl = null,
} = {}) {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("You must be signed in to contact support.");
  }
  const userId = user.uid;

  let screenshotPath = "";
  let screenshotBucket = "";
  if (screenshotDataUrl) {
    try {
      const path = `supportScreenshots/${userId}/${Date.now()}.jpg`;
      const storageRef = ref(storage, path);
      await uploadString(storageRef, screenshotDataUrl, "data_url", {
        contentType: "image/jpeg",
      });
      screenshotPath = path;
      screenshotBucket = storageRef.bucket || "";
    } catch (err) {
      // A failed upload shouldn't block the ticket — send it without the image.
      console.warn("[support] screenshot upload failed:", err?.message || err);
      screenshotPath = "";
      screenshotBucket = "";
    }
  }

  const device = typeof navigator !== "undefined" ? navigator.userAgent : "";
  const platform = (() => {
    try {
      return Capacitor?.getPlatform?.() || "web";
    } catch {
      return "web";
    }
  })();

  const docRef = await authedWrite(
    () =>
      addDoc(collection(db, "supportTickets"), {
        userId,
        userEmail: user.email || "",
        description: String(description || "").slice(0, 5000),
        context: String(context || ""),
        sessionId: String(sessionId || ""),
        appVersion: APP_VERSION,
        device,
        platform,
        screenshotPath,
        screenshotBucket,
        createdAt: serverTimestamp(),
      }),
    "support-ticket"
  );

  return { ticketId: docRef.id, hadScreenshot: !!screenshotPath };
}
